"use client";

import { create } from "zustand";
import type { Conta, Empresa, MatrizPermissoes, Modulo, Papel, Permissao, Sessao, Usuario } from "@/types";
import { sessionStore, uiStorage } from "@/lib/storage";

const KEY = "tms.sessao";
// Última empresa escolhida: a aba nova (sessão renovada pelo cookie) abre nela
const KEY_ULTIMA_EMPRESA = "tms.ultimaEmpresa";

/** Avisa as outras abas do mesmo navegador (logout em uma = logout em todas). */
const canal: BroadcastChannel | null =
  typeof window !== "undefined" && "BroadcastChannel" in window ? new BroadcastChannel("tms.sessao") : null;

interface SessionState {
  token: string | null;
  usuario: Usuario | null;
  empresas: Empresa[];
  empresaId: string | null;
  conta: Conta | null;
  expirada: boolean;
  hydrated: boolean;
  hydrate: () => void;
  start: (s: Sessao) => void;
  selectEmpresa: (id: string) => void;
  updateEmpresa: (e: Empresa) => void;
  updateUsuario: (u: Partial<Usuario>) => void;
  /** Atualiza usuário/empresas/conta a partir de GET /auth/me. */
  refresh: (s: Omit<Sessao, "token">) => void;
  /** Token novo vindo de POST /auth/refresh (cookie de sessão). */
  renovar: (s: Sessao) => void;
  logout: () => void;
  expire: () => void;
}

type Persistido = Pick<SessionState, "token" | "usuario" | "empresas" | "empresaId" | "conta">;

function persist(s: Persistido) {
  if (s.token) sessionStore.set(KEY, s);
  else sessionStore.remove(KEY);
}

export const useSession = create<SessionState>((set, get) => ({
  token: null,
  usuario: null,
  empresas: [],
  empresaId: null,
  conta: null,
  expirada: false,
  hydrated: false,
  hydrate: () => {
    if (get().hydrated) return;
    const saved = sessionStore.get<Persistido>(KEY);
    set({ ...(saved ?? {}), hydrated: true });
  },
  start: (s) => {
    const empresaId = s.empresas.length === 1 ? s.empresas[0].id : null;
    const next = { token: s.token, usuario: s.usuario, empresas: s.empresas, empresaId, conta: s.conta ?? null };
    persist(next);
    set({ ...next, expirada: false });
  },
  refresh: (s) => {
    const atual = get().empresaId;
    // Mantém a empresa selecionada se ela ainda estiver acessível
    const empresaId = s.empresas.some((e) => e.id === atual) ? atual : s.empresas.length === 1 ? s.empresas[0].id : null;
    set({ usuario: s.usuario, empresas: s.empresas, empresaId, conta: s.conta ?? null });
    persist(get());
  },
  renovar: (s) => {
    const atual = get().empresaId ?? uiStorage.get<string | null>(KEY_ULTIMA_EMPRESA, null);
    const empresaId = s.empresas.some((e) => e.id === atual) ? atual : s.empresas.length === 1 ? s.empresas[0].id : null;
    set({ token: s.token, usuario: s.usuario, empresas: s.empresas, empresaId, conta: s.conta ?? null, expirada: false });
    persist(get());
  },
  selectEmpresa: (id) => {
    uiStorage.set(KEY_ULTIMA_EMPRESA, id);
    set({ empresaId: id });
    persist(get());
  },
  updateEmpresa: (e) => {
    set({ empresas: get().empresas.map((x) => (x.id === e.id ? e : x)) });
    persist(get());
  },
  updateUsuario: (u) => {
    const cur = get().usuario;
    if (!cur) return;
    set({ usuario: { ...cur, ...u } });
    persist(get());
  },
  logout: () => {
    const next = { token: null, usuario: null, empresas: [], empresaId: null, conta: null };
    persist(next);
    set({ ...next, expirada: false });
    canal?.postMessage("logout");
  },
  expire: () => {
    if (!get().token) return;
    const next = { token: null, usuario: null, empresas: [], empresaId: null, conta: null };
    persist(next);
    set({ ...next, expirada: true });
  },
}));

canal?.addEventListener("message", (e: MessageEvent) => {
  if (e.data !== "logout" || !useSession.getState().token) return;
  const next = { token: null, usuario: null, empresas: [], empresaId: null, conta: null };
  persist(next);
  // Sem broadcast de volta; o app volta para o login ao perder o token
  useSession.setState({ ...next, expirada: false });
});

/** Sem conta (modo 100% mock) o acesso é total. */
export function useAcesso() {
  return useSession((s) => s.conta?.acesso ?? "TOTAL");
}

export function useEmpresa(): Empresa | null {
  return useSession((s) => s.empresas.find((e) => e.id === s.empresaId) ?? null);
}

/* ---------- Permissões ---------- */

export type Acao =
  | "viagem.emitir"
  | "viagem.cancelar"
  | "doc.cancelar"
  | "cadastro.editar"
  | "financeiro.ver"
  | "financeiro.quitar"
  | "sistema.ver"
  | "usuarios.ver"
  | "tributos.editar";

const REGRAS: Record<Papel, Acao[]> = {
  admin: [
    "viagem.emitir",
    "viagem.cancelar",
    "doc.cancelar",
    "cadastro.editar",
    "financeiro.ver",
    "financeiro.quitar",
    "sistema.ver",
    "usuarios.ver",
    "tributos.editar",
  ],
  operador: ["viagem.emitir", "viagem.cancelar", "doc.cancelar", "cadastro.editar"],
  financeiro: ["financeiro.ver", "financeiro.quitar"],
  leitura: ["financeiro.ver"],
};

/** Cada ação do front corresponde a uma célula da matriz papel × módulo × permissão da API. */
const NA_MATRIZ: Record<Acao, [Modulo, Permissao]> = {
  "viagem.emitir": ["viagens", "emitir"],
  "viagem.cancelar": ["viagens", "cancelar"],
  "doc.cancelar": ["documentos", "cancelar"],
  "cadastro.editar": ["cadastros", "editar"],
  "financeiro.ver": ["financeiro", "ver"],
  "financeiro.quitar": ["financeiro", "editar"],
  "sistema.ver": ["sistema", "ver"],
  "usuarios.ver": ["usuarios", "ver"],
  "tributos.editar": ["sistema", "editar"],
};

/** Com a API real, vale a matriz da conta (a mesma que a API aplica); no mock, as REGRAS fixas. */
export function can(papel: Papel | undefined, acao: Acao, matriz: MatrizPermissoes | null | undefined = useSession.getState().conta?.permissoes) {
  if (!papel) return false;
  if (papel === "admin") return true;
  if (matriz) {
    const [modulo, permissao] = NA_MATRIZ[acao];
    return matriz[papel]?.[modulo]?.includes(permissao) ?? false;
  }
  return REGRAS[papel].includes(acao);
}

export function useCan(acao: Acao) {
  return useSession((s) => can(s.usuario?.papel, acao, s.conta?.permissoes));
}
